import { Component, DestroyRef, OnInit, Input, ViewChild, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GridComponent, ActionEventArgs, DataStateChangeEventArgs, PageService, SortService, FilterService, ResizeService, FreezeService, FilterSettingsModel, RowDataBoundEventArgs, RecordClickEventArgs } from '@syncfusion/ej2-angular-grids';
import { AuditService } from '../shared/services/audit.service';
import { Audit } from '../shared/models/audit';
import type { AuditDownloadKey, AuditDownloadSelection, AuditPageRequest, PagedResult } from '../shared/models/paged-result';
import { DownloadJobService } from '../shared/services/download-job.service';
import { GridSelection } from '../shared/utils/grid-selection';
import { MatDialog } from '@angular/material/dialog';
import { ViewAuditComponent } from '../view-audit/view-audit.component';
import { RequestsService } from '../shared/services/requests.service';
import { AttachmentData, RequestDocAttachmentResponse } from '../shared/models/requestDocAttachment';
import { UserService } from '../shared/services/user.service';
import { UserDetails } from '../shared/models/user-details';
import { CommonService } from '../shared/services/common.service';
import { SnackbarService } from '../shared/services/snackbar.service';
import { RequestType, RequestTypeOrder } from '../enums/request-type.enum';
import { RpaStatus } from '../enums/rpa-status.enum';
import { AUDIT_TAB_KEY, TabFilterStateService } from '../shared/services/tab-filter-state.service';
import { AUDIT_DEFAULT_PAGE_SIZE, buildAuditPageRequest } from '../shared/utils/audit-grid-request';
import { sortKeysOf } from '../shared/utils/grid-request';
import { answerFilterPopup, isFilterPopupRequest } from '../shared/utils/grid-filter-popup';

@Component({
    selector: 'xp-audit-log',
    templateUrl: './audit-log.component.html',
    styleUrl: './audit-log.component.scss',
    providers: [PageService, SortService, FilterService, ResizeService, FreezeService]
})
export class AuditLogComponent implements OnInit {
    @Input() requestId?: number;
    @Input() requestType?: string;
    showPaginator = input<boolean>(true);

    RpaStatus = RpaStatus;
    /** Per-request mode: the whole history of one request, paged by the grid in the browser. */
    auditData: Audit[] = [];
    /** Global mode: the current server page and the total number of rows matching the filters. */
    gridData: { result: Audit[]; count: number } = { result: [], count: 0 };
    dataLoading = true;
    noAuditData = false;
    loadError = false;
    auditCount = 0;
    fileToBeDownloaded!: RequestDocAttachmentResponse;
    hasAccessToViewLogs = false;
    userDetails: UserDetails | undefined;
    selection = new GridSelection();

    filterSettings: FilterSettingsModel = { type: 'Menu' };
    pageSettings = { pageSize: AUDIT_DEFAULT_PAGE_SIZE, pageSizes: [20, 50, 100] };

    @ViewChild('grid') grid!: GridComponent;

    /** Checkbox filter lists that do not depend on the data: the values are fixed. */
    private readonly staticFilterValues: Record<string, string[]> = {
        requestTypeName: Object.values(RequestType),
        rpaStatus: Object.values(RpaStatus)
    };

    /** Identifies the newest page request, so a slower, older response can never overwrite a newer one. */
    private requestSeq = 0;
    private initialLoadStarted = false;
    private lastFiltersKey = '';

    constructor(
        private auditService: AuditService,
        private dialog: MatDialog,
        private requestService: RequestsService,
        private userService: UserService,
        private commonService: CommonService,
        private tabFilterState: TabFilterStateService,
        private snackbar: SnackbarService,
        private downloadJobService: DownloadJobService,
        private destroyRef: DestroyRef) { }

    ngOnInit(): void {
        if (this.globalAudit) {
            this.filterSettings = this.tabFilterState.getFilterSettings(AUDIT_TAB_KEY);
            void this.downloadJobService.initialize();
        }
        this.userService.hasAccessToViewLogs$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(access => {
            this.hasAccessToViewLogs = access;
            this.userDetails = this.userService.userDetails;
            // The server works out from the token what the caller may see, so the Audit Tab loads once;
            // the flag re-emitting when the nav menu finishes loading must not start a second load.
            if (this.globalAudit && this.initialLoadStarted) return;
            this.initialLoadStarted = true;
            this.loadAuditData();
        });
    }

    get globalAudit(): boolean {
        return this.requestId == undefined;
    }

    get showPaymentBankColumns(): boolean {
        return !this.globalAudit && this.requestType === RequestType.Payment.toString();
    }

    /** What the grid is bound to: the server page in global mode, the whole history otherwise. */
    get gridSource(): Audit[] | { result: Audit[]; count: number } {
        return this.globalAudit ? this.gridData : this.auditData;
    }

    /** The number of rows the grid's filters match: the server's total in global mode. */
    get totalRows(): number {
        return this.globalAudit ? this.gridData.count : this.auditData.length;
    }

    private loadAuditData(): void {
        this.dataLoading = true;

        if (this.requestId && this.requestType) {
            this.auditService.getUserAuditByRequest(
                this.requestId,
                RequestTypeOrder[this.requestType as RequestType]
            )
                .then((res: Audit[]) => this.handleAuditResponse(res))
                .catch(() => { this.dataLoading = false; this.noAuditData = true; });
        } else {
            // First page, with any filters remembered from the last visit applied from the very first request.
            const request = buildAuditPageRequest({
                skip: 0,
                take: this.pageSettings.pageSize,
                filters: this.filterSettings.columns ?? []
            });
            this.lastFiltersKey = JSON.stringify(request.filters);
            this.loadGlobalPage(request);
            this.loadAuditCount();
        }
    }

    /** Per-request mode: the history arrives whole and the grid pages it. */
    private handleAuditResponse(res: Audit[]): void {
        this.dataLoading = false;
        this.noAuditData = !res || res.length === 0;
        this.selection.clear();
        if (!res) res = [];
        this.convertDatesToLocal(res);
        this.auditData = res;
    }

    private convertDatesToLocal(rows: Audit[]): void {
        rows.forEach(audit => {
            if (audit.createdDate) audit.createdDate = this.commonService.convertUtcToLocal(audit.createdDate);
            if (audit.updatedDate) audit.updatedDate = this.commonService.convertUtcToLocal(audit.updatedDate);
        });
    }

    /** Global mode: asks the server for one page; only the newest request's answer is shown. */
    private loadGlobalPage(request: AuditPageRequest): void {
        const seq = ++this.requestSeq;
        this.dataLoading = true;
        this.loadError = false;

        this.auditService.getUserAuditPage(request)
            .then(res => { if (seq === this.requestSeq) this.showPage(res); })
            .catch(() => { if (seq === this.requestSeq) this.showPageError(); });
    }

    private showPage(res: PagedResult<Audit>): void {
        const rows = res.items ?? [];
        this.convertDatesToLocal(rows);
        this.dataLoading = false;
        this.noAuditData = res.totalCount === 0;
        // A new object, not a changed one: the grid is waiting for it to finish the paging/sorting/filtering it asked for.
        this.gridData = { result: rows, count: res.totalCount };

        // The server moves a page past the end back to the last page; keep the pager on the page it actually shows.
        if (this.grid && this.grid.pageSettings.currentPage !== res.page) {
            this.grid.pageSettings.currentPage = res.page;
        }
    }

    private showPageError(): void {
        this.dataLoading = false;
        this.loadError = true;
        // Hand the grid back what it already had so it stops waiting; the previous page stays visible.
        this.gridData = { result: this.gridData.result, count: this.gridData.count };
        this.snackbar.errorSnackBar('Unable to load the audit logs. Please try again.');
    }

    /** Retry after a failed load, for the page, sort and filters the grid is currently showing. */
    retryLoad(): void {
        this.loadGlobalPage(this.currentGridRequest());
    }

    private loadAuditCount(): void {
        this.auditService.getRejectedWithdrawnCount(this.userDetails?.userEmail ?? '', this.userDetails?.tenantId ?? '')
            .then(count => { this.auditCount = count; })
            .catch(err => console.error('Failed to load audit count', err));
    }

    /** The request for the page, sort and filters the grid is showing right now. */
    private currentGridRequest(): AuditPageRequest {
        const pageSize = this.grid?.pageSettings.pageSize ?? this.pageSettings.pageSize;
        const currentPage = this.grid?.pageSettings.currentPage ?? 1;
        return buildAuditPageRequest({
            skip: (currentPage - 1) * pageSize,
            take: pageSize,
            sorted: sortKeysOf(this.grid),
            filters: this.grid?.filterSettings.columns ?? this.filterSettings.columns ?? []
        });
    }

    /** The grid asks for data: a page, sort or filter change, or the contents of a checkbox filter. */
    onDataStateChange(state: DataStateChangeEventArgs): void {
        if (!this.globalAudit) return;

        if (isFilterPopupRequest(state)) {
            answerFilterPopup(state, field => this.filterValues(field));
            return;
        }

        const request = buildAuditPageRequest({
            skip: state.skip,
            take: state.take,
            // The state argument lists the sort keys lowest priority first, so they are read from the grid instead.
            sorted: sortKeysOf(this.grid),
            // The grid has already added the new filter to its settings when it asks for the data.
            filters: this.grid?.filterSettings.columns ?? []
        });

        // Remember the filters now: in this server-driven mode the grid does not raise actionComplete once the data arrives.
        this.rememberFilters();

        // Rows selected under one set of filters must not stay selected, unseen, under another.
        const filtersKey = JSON.stringify(request.filters);
        if (filtersKey !== this.lastFiltersKey) {
            this.lastFiltersKey = filtersKey;
            this.selection.clear();
        }

        this.loadGlobalPage(request);
    }

    /** Every value of a column for its checkbox filter list: fixed for some columns, from the server for the rest. */
    private filterValues(field: string): Promise<string[]> {
        return this.staticFilterValues[field]
            ? Promise.resolve(this.staticFilterValues[field])
            : this.auditService.getUserAuditFilterValues(field);
    }

    rowDataBound(args: RowDataBoundEventArgs) {
        const data = args.data as Audit;
        if (data.rpaStatus === RpaStatus.Failed) {
            (args.row as HTMLElement).classList.add('rpa-failed-row');
        }
        (args.row as HTMLElement).classList.toggle('selected-row', this.globalAudit && this.isSelected(data));
    }

    onRecordClick(args: RecordClickEventArgs): void {
        if (!this.globalAudit) return;
        const target = args.target as HTMLElement;
        if (target.closest('button')) return;

        const data = args.rowData as Audit;
        this.toggleSelection(data);
        this.highlightRow(data);
    }

    onCheckboxClick(event: MouseEvent, data: Audit): void {
        event.stopPropagation();
        this.toggleSelection(data);
        this.highlightRow(data);
    }

    private highlightRow(data: Audit): void {
        if (!this.grid) return;
        const records = this.grid.getCurrentViewRecords() as Audit[];
        const index = records.findIndex(row => this.getRowKey(row) === this.getRowKey(data));
        if (index === -1) return;
        (this.grid.getRowByIndex(index) as HTMLElement)?.classList.toggle('selected-row', this.isSelected(data));
    }

    private applySelectionClassToVisibleRows(): void {
        if (!this.grid) return;
        const records = this.grid.getCurrentViewRecords() as Audit[];
        const rows = this.grid.getRows();
        records.forEach((row, index) => {
            (rows[index] as HTMLElement)?.classList.toggle('selected-row', this.isSelected(row));
        });
    }

    onActionComplete(args: ActionEventArgs): void {
        if (!this.globalAudit || args.requestType !== 'filtering') return;
        this.rememberFilters();
    }

    /**
     * Keeps the grid's filters for when the tab is opened again. The grid's own filter objects hide their values behind
     * accessors, so a plain copy of them comes out empty; the values are read out one by one instead.
     */
    private rememberFilters(): void {
        const filters = (this.grid?.filterSettings.columns ?? []).map(column => ({
            field: column.field,
            operator: column.operator,
            value: column.value,
            predicate: column.predicate,
            matchCase: column.matchCase,
            ignoreAccent: column.ignoreAccent
        }));
        this.tabFilterState.set(AUDIT_TAB_KEY, filters);
    }

    resetFilters() {
        const hadFilters = (this.grid?.filterSettings.columns?.length ?? 0) > 0;
        this.selection.clear();
        this.tabFilterState.clearFiltering(AUDIT_TAB_KEY, this.grid);
        // Clearing filters makes the grid ask for the data itself; with no filter to clear it asks for nothing, so reload here.
        if (!hadFilters) {
            this.loadGlobalPage(this.currentGridRequest());
        }
    }

    /** Downloads a ZIP of the PDFs of every request matching the current filters, however many there are. */
    downloadAllAsPdf(): void {
        this.startDownload({
            allMatching: true,
            filters: this.currentGridRequest().filters,
            keys: [],
            excludedKeys: []
        });
    }

    /** Downloads a ZIP of the PDFs of the selected requests, wherever in the list they are. */
    downloadSelectedAsPdf(): void {
        if (this.selectedCount === 0) return;

        this.startDownload({
            allMatching: this.selection.allMatching,
            filters: this.selection.allMatching ? this.currentGridRequest().filters : [],
            keys: this.selection.allMatching ? [] : this.selection.tickedKeys.map(key => this.parseRowKey(key)),
            excludedKeys: this.selection.allMatching ? this.selection.excludedKeys.map(key => this.parseRowKey(key)) : []
        });
    }

    /** Asks the server to prepare the ZIP in the background; the finished ZIP is saved by the DownloadJobService. */
    private startDownload(selection: AuditDownloadSelection): void {
        void this.downloadJobService.begin(() => this.auditService.startDownload(selection));
    }

    /** True while a ZIP is being started or prepared; a user may have only one at a time. */
    get downloadBusy(): boolean {
        return this.downloadJobService.inProgress;
    }

    /** The number of requests selected, across every page. */
    get selectedCount(): number {
        return this.selection.count(this.gridData.count);
    }

    getRowKey(row: Audit): string {
        return `${row.requestId}_${row.requestTypeName}`;
    }

    /** Reads a key made by {@link getRowKey} back into the request it names. */
    private parseRowKey(key: string): AuditDownloadKey {
        const separator = key.indexOf('_');
        return { requestId: Number(key.slice(0, separator)), requestType: key.slice(separator + 1) };
    }

    isSelected(row: Audit): boolean {
        return this.selection.isSelected(this.getRowKey(row));
    }

    toggleSelection(row: Audit): void {
        this.selection.toggle(this.getRowKey(row));
    }

    /** Selects every request matching the filters, on every page, not only the rows shown. */
    selectAll(): void {
        this.selection.selectAllMatching();
    }

    unselectAll(): void {
        this.selection.clear();
    }

    /** True when every row on this page is selected. */
    get allSelected(): boolean {
        const rows = this.getVisibleRows();
        return rows.length > 0 && rows.every(row => this.isSelected(row));
    }

    get someSelected(): boolean {
        return !this.allSelected && this.getVisibleRows().some(row => this.isSelected(row));
    }

    /** The header checkbox: ticking it selects every matching request on every page; unticking clears the selection. */
    toggleSelectAll(checked: boolean): void {
        if (checked) {
            this.selectAll();
        } else {
            this.unselectAll();
        }
        this.applySelectionClassToVisibleRows();
    }

    /** The rows on the page being shown; the server holds the rest. */
    private getVisibleRows(): Audit[] {
        return this.gridData.result;
    }

    viewAuditLogs(row: Audit) {
        const dialogRef = this.dialog.open(ViewAuditComponent, {
            disableClose: true,
            data: row, width: "70%", height: "90%", maxHeight: "80vh", maxWidth: "80vw", panelClass: 'full-height-dialog'
        });
        dialogRef.afterClosed().subscribe(() => { });
    }

    downloadDocuments(requestId: number, requestType: string) {
        this.requestService.getRequestDocumentList(requestId, requestType).then((res) => {
            this.fileToBeDownloaded = res;
            this.downloadMultipleBase64Files(this.fileToBeDownloaded.attachments ?? []);
        });
    }

    downloadMultipleBase64Files(files: AttachmentData[]): void {
        files.forEach(file => {
            const { attachments, fileName } = file;
            const byteCharacters = atob(attachments);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/octet-stream' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
        });
    }
}
